import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Calendar, MapPin, ArrowRight, Users } from "lucide-react";

export default function CareerEvents() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/career-events").then(r => setRows(r.data)); }, []);
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <div className="max-w-7xl mx-auto px-6 py-14" data-testid="career-events-page">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Career Events</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-[#0A2540] mt-2">Learn. Network. Grow.</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">Webinars, workshops, campus drives and meetups hosted by VS-Solutions Careers. Register to join and get reminders.</p>
        {rows.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <p className="text-slate-500">No upcoming career events. Check back soon.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {rows.map(e => (
              <Link key={e.id} to={`/career-events/${e.id}`} className="block group" data-testid={`event-card-${e.id}`}>
                <Card className="overflow-hidden hover:shadow-lg transition-all border-slate-200 h-full">
                  <div className="h-40 bg-slate-100 relative overflow-hidden">
                    {e.banner_url ? <img src={e.banner_url} alt={e.name} className="w-full h-full object-cover"/> :
                      <div className="w-full h-full vs-hero-gradient flex items-center justify-center text-white"><Calendar className="w-10 h-10 opacity-60"/></div>}
                    {e.featured && <Badge className="absolute top-3 left-3 bg-[#F59E0B] text-[#0A2540] border-none">Featured</Badge>}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Badge className="bg-slate-100 text-slate-700 border-none">{e.event_type}</Badge>
                      <Badge className="bg-blue-50 text-blue-700 border-none">{e.mode}</Badge>
                    </div>
                    <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3 group-hover:text-[#2563EB] transition-colors">{e.name}</h3>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{e.description}</p>
                    <div className="mt-3 space-y-1 text-xs text-slate-500">
                      {e.event_start && <div><Calendar className="w-3 h-3 inline mr-1"/>{new Date(e.event_start).toLocaleDateString()}</div>}
                      {e.location && <div><MapPin className="w-3 h-3 inline mr-1"/>{e.location}</div>}
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CareerEventDetail() {
  const id = window.location.pathname.split("/").pop();
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get(`/career-events/${id}`).then(r => setE(r.data)).catch(()=>{}); }, [id]);
  const register = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/career-events/${id}/register`);
      setMsg(data.duplicate ? "You are already registered." : `Registration ${data.registration.status}!`);
    } catch (err) {
      setMsg(err.response?.data?.detail || "Registration failed. Please sign in as a candidate.");
    } finally { setLoading(false); }
  };
  if (!e) return <div><PublicHeader/><div className="p-10 text-slate-500">Loading…</div></div>;
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader/>
      <div className="max-w-4xl mx-auto px-6 py-14" data-testid="event-detail-page">
        <Link to="/career-events" className="text-sm text-[#2563EB]">← All events</Link>
        {e.banner_url && <img src={e.banner_url} alt={e.name} className="w-full h-64 object-cover rounded-lg mt-6"/>}
        <div className="mt-6 flex flex-wrap gap-2">
          <Badge className="bg-slate-100 text-slate-700 border-none">{e.event_type}</Badge>
          <Badge className="bg-blue-50 text-blue-700 border-none">{e.mode}</Badge>
          <Badge className="bg-emerald-50 text-emerald-700 border-none">{e.status}</Badge>
        </div>
        <h1 className="font-display text-4xl font-bold text-[#0A2540] mt-4">{e.name}</h1>
        <p className="text-slate-600 mt-4">{e.description}</p>
        {e.full_description && <div className="text-slate-700 mt-4 whitespace-pre-wrap leading-relaxed">{e.full_description}</div>}
        <Card className="mt-8 p-6 border-slate-200">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            {e.event_start && <div><span className="text-slate-500">When:</span> <strong className="text-[#0A2540]">{new Date(e.event_start).toLocaleString()}</strong> <span className="text-slate-400">({e.timezone})</span></div>}
            {e.location && <div><span className="text-slate-500">Location:</span> <strong className="text-[#0A2540]">{e.location}</strong></div>}
            {e.eligibility && <div><span className="text-slate-500">Eligibility:</span> {e.eligibility}</div>}
            {e.organizer && <div><span className="text-slate-500">Organizer:</span> {e.organizer}</div>}
            <div><span className="text-slate-500">Capacity:</span> {e.registered_count}/{e.capacity} · <span className="text-emerald-700 font-medium">{e.remaining_seats} seats left</span></div>
            {e.registration_deadline && <div><span className="text-slate-500">Register by:</span> {new Date(e.registration_deadline).toLocaleDateString()}</div>}
          </div>
          {(e.skills || []).length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Skills / Topics</div>
              <div className="flex flex-wrap gap-1.5">{e.skills.map(s => <Badge key={s} className="bg-slate-100 text-slate-700 border-none">{s}</Badge>)}</div>
            </div>
          )}
          <div className="mt-6 flex items-center gap-3">
            <Button onClick={register} disabled={loading || e.remaining_seats === 0} className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="event-register-btn">
              {loading ? "Registering…" : e.remaining_seats === 0 ? "Event Full" : "Register"} <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
            {msg && <span className="text-sm text-emerald-700" data-testid="event-register-msg">{msg}</span>}
          </div>
        </Card>
      </div>
    </div>
  );
}
